// ---------------------------------------------------------------
// scheduleService.js — Horarios mensuales importados desde Excel.
// Colección `schedules`, un documento por usuario y mes con id
// `${uid}_${YYYY-MM}`. Sólo el administrador escribe; cada usuario
// lee únicamente el suyo (ver firestore.rules).
// ---------------------------------------------------------------
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { sharedSubscribe } from "../utils/sharedSnapshot";
import { savePresence, savePresencePartial, savePresenceForUser } from "./presenceService";
import { avisarCambioHorario, avisarCambiosHorario } from "./scheduleNoticeService";

/** Los `days` que tiene ahora cada persona en un mes (para el aviso) */
async function horariosDelMes(key) {
  const snap = await getDocs(
    query(collection(db, "schedules"), where("month", "==", key))
  );
  const porUid = {};
  snap.docs.forEach((d) => (porUid[d.data().userId] = d.data().days || {}));
  return porUid;
}

/** "2026-07" a partir de año y mes (1-12) */
export const monthKey = (year, month) =>
  `${year}-${String(month).padStart(2, "0")}`;

export async function saveSchedule(uid, key, days) {
  const totalHours = Object.values(days).reduce(
    (sum, d) => sum + (d.hours || 0),
    0
  );
  // Lo que había, para avisar a la persona de qué ha cambiado
  const antes = await getDoc(doc(db, "schedules", `${uid}_${key}`))
    .then((snap) => (snap.exists() ? snap.data().days : null))
    .catch(() => undefined);
  await setDoc(doc(db, "schedules", `${uid}_${key}`), {
    userId: uid,
    month: key,
    days,
    totalHours,
    updatedAt: serverTimestamp(),
  });
  // Resumen sin datos personales para que lo vea el equipo
  await savePresenceForUser(key, uid, days);
  // Sin esperar: el aviso no debe frenar el guardado. Si no se pudo
  // leer lo de antes, no se avisa (no se sabría qué ha cambiado).
  if (antes !== undefined) avisarCambioHorario(key, uid, antes, days);
}

export function deleteSchedule(uid, key) {
  return deleteDoc(doc(db, "schedules", `${uid}_${key}`));
}

export function subscribeSchedule(uid, key, onData, onError) {
  return sharedSubscribe(
    `schedule:${uid}_${key}`,
    (cb, err) =>
      onSnapshot(
        doc(db, "schedules", `${uid}_${key}`),
        (snap) => cb(snap.exists() ? snap.data() : null),
        err
      ),
    onData,
    onError
  );
}

/** Guarda de una vez el horario mensual de varios usuarios (generador) */
export async function saveMonthSchedulesBatch(key, daysByUid) {
  const antes = await horariosDelMes(key).catch(() => null);
  const batch = writeBatch(db);
  for (const [uid, days] of Object.entries(daysByUid)) {
    const totalHours =
      Math.round(
        Object.values(days).reduce((sum, d) => sum + (d.hours || 0), 0) * 100
      ) / 100;
    batch.set(doc(db, "schedules", `${uid}_${key}`), {
      userId: uid,
      month: key,
      days,
      totalHours,
      updatedAt: serverTimestamp(),
    });
  }
  await batch.commit();
  // Y el resumen de presencia del mes, sin bajas ni vacaciones
  await savePresence(key, daysByUid);
  if (antes) avisarCambiosHorario(key, antes, daysByUid);
}

/**
 * Igual que `saveMonthSchedulesBatch` pero SOLO para las personas que
 * se indiquen: el horario del resto se queda exactamente como estaba.
 *
 * Existe porque el generador construye el mes entero, y cuando alguien
 * se incorpora a mitad de temporada hay que rehacer su horario (y el de
 * quien rote con ella) sin tirar los retoques a mano que ya tenga
 * guardado el resto del equipo. La presencia se mezcla en vez de
 * reescribirse, por el mismo motivo.
 */
export async function saveMonthSchedulesForUsers(key, daysByUid, uids) {
  const elegidos = new Set(uids || []);
  const parcial = Object.fromEntries(
    Object.entries(daysByUid || {}).filter(([uid]) => elegidos.has(uid))
  );
  if (!Object.keys(parcial).length) return 0;

  const antes = await horariosDelMes(key).catch(() => null);
  const batch = writeBatch(db);
  for (const [uid, days] of Object.entries(parcial)) {
    const totalHours =
      Math.round(
        Object.values(days).reduce((sum, d) => sum + (d.hours || 0), 0) * 100
      ) / 100;
    batch.set(doc(db, "schedules", `${uid}_${key}`), {
      userId: uid,
      month: key,
      days,
      totalHours,
      updatedAt: serverTimestamp(),
    });
  }
  await batch.commit();
  await savePresencePartial(key, parcial);
  if (antes) avisarCambiosHorario(key, antes, parcial);
  return Object.keys(parcial).length;
}

/**
 * Borra TODOS los horarios de un mes (todas las personas). Los
 * contadores anuales se recalculan solos: escuchan la colección en
 * tiempo real. Devuelve cuántos horarios se eliminaron.
 */
export async function deleteMonthSchedules(key) {
  const snap = await getDocs(
    query(collection(db, "schedules"), where("month", "==", key))
  );
  if (snap.empty) return 0;
  const batch = writeBatch(db);
  snap.docs.forEach((d) => batch.delete(d.ref));
  await batch.commit();
  return snap.size;
}

/* -----------------------------------------------------------------
   Configuración del generador (roles + estado de rotación)
   Documento único: scheduleConfig/main (sólo administradores).
   ----------------------------------------------------------------- */
export function subscribeScheduleConfig(onData, onError) {
  return onSnapshot(
    doc(db, "scheduleConfig", "main"),
    (snap) => onData(snap.exists() ? snap.data() : null),
    onError
  );
}

export function saveScheduleConfig(data) {
  return setDoc(
    doc(db, "scheduleConfig", "main"),
    { ...data, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/* -----------------------------------------------------------------
   Guardias 24h: al marcar guardia a un farmacéutico
   1. Su día queda con el turno 15:00 → 09:00 (día siguiente).
   2. El día siguiente pasa a descanso obligatorio (también si cae
      en el mes siguiente).
   3. Opcionalmente, otra persona (Judith/Houda, alternando) cubre
      la mañana 09:00-15:00 de ese día conservando sus turnos de
      tarde. Se guarda quién cubrió para alternar la próxima vez.
   ----------------------------------------------------------------- */
export async function setGuardDay({
  uid,
  year,
  month,
  currentDays,
  dayNum,
  dayData,
  coverUid,
}) {
  const key = monthKey(year, month);
  const daysInMonth = new Date(year, month, 0).getDate();
  const restDay = {
    shifts: [],
    hours: 0,
    baja: false,
    vacation: null,
    guardRest: true,
  };

  // 1 + 2 (mismo mes)
  const newDays = { ...(currentDays || {}), [String(dayNum)]: dayData };
  if (dayNum + 1 <= daysInMonth) {
    newDays[String(dayNum + 1)] = restDay;
  }
  await saveSchedule(uid, key, newDays);

  // 2 (si el descanso cae en el mes siguiente)
  if (dayNum + 1 > daysInMonth) {
    const nextKey = monthKey(month === 12 ? year + 1 : year, (month % 12) + 1);
    const snap = await getDoc(doc(db, "schedules", `${uid}_${nextKey}`));
    const nextDays = snap.exists() ? { ...snap.data().days } : {};
    nextDays["1"] = restDay;
    await saveSchedule(uid, nextKey, nextDays);
  }

  // 3. Cobertura de la mañana
  if (coverUid) {
    const snap = await getDoc(doc(db, "schedules", `${coverUid}_${key}`));
    const coverDays = snap.exists() ? { ...snap.data().days } : {};
    const existing = coverDays[String(dayNum)];
    // Mantiene sus turnos que empiecen a partir de las 15:00
    const keep = (existing?.shifts || []).filter(
      (s) => Number(s.start.split(":")[0]) >= 15
    );
    const shifts = [{ start: "09:00", end: "15:00" }, ...keep];
    const hours =
      Math.round(
        shifts.reduce((sum, s) => {
          const [h1, m1] = s.start.split(":").map(Number);
          const [h2, m2] = s.end.split(":").map(Number);
          return sum + (h2 * 60 + m2 - (h1 * 60 + m1)) / 60;
        }, 0) * 100
      ) / 100;
    coverDays[String(dayNum)] = {
      shifts,
      hours,
      baja: false,
      vacation: existing?.vacation || null,
      guardCover: true,
    };
    await saveSchedule(coverUid, key, coverDays);
    await saveScheduleConfig({ lastGuardCoverUid: coverUid });
  }
}

/** Los 12 horarios de UN usuario en un año (perfil y pantalla de
 *  pausa: cada empleado puede leer los suyos). Igualdades solas →
 *  no necesita índice compuesto. */
export function subscribeUserYearSchedules(uid, year, onData, onError) {
  const months = Array.from({ length: 12 }, (_, i) => monthKey(year, i + 1));
  const q = query(
    collection(db, "schedules"),
    where("userId", "==", uid),
    where("month", "in", months)
  );
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => d.data())),
    onError
  );
}

/** Los doce meses de UNA persona, de una sola vez (para imprimir el
 *  año entero). Misma forma de consulta que `subscribeUserYearSchedules`,
 *  así que las reglas y el índice ya la admiten. */
export async function getUserYearSchedulesOnce(uid, year) {
  // Se piden TODOS los meses de esa persona y se filtra el año aquí.
  // Con un `where("month","in",[...12])` haría falta un índice
  // compuesto y, si algún día no está creado, la consulta falla en
  // silencio justo cuando alguien intenta imprimir. Son una docena de
  // documentos por persona: no compensa el riesgo.
  const snap = await getDocs(
    query(collection(db, "schedules"), where("userId", "==", uid))
  );
  return snap.docs
    .map((d) => d.data())
    .filter((d) => String(d.month || "").startsWith(`${year}-`));
}

/** Lectura puntual de un horario mensual (pantalla de pausa) */
export async function getScheduleOnce(uid, key) {
  const snap = await getDoc(doc(db, "schedules", `${uid}_${key}`));
  return snap.exists() ? snap.data() : null;
}

/** Lectura puntual de los horarios de UN mes de todo el equipo
 *  (continuidad del generador con el mes anterior) */
export async function getMonthSchedulesOnce(key) {
  const snap = await getDocs(
    query(collection(db, "schedules"), where("month", "==", key))
  );
  return snap.docs.map((d) => d.data());
}

/** Lectura puntual de TODOS los horarios de un año (volcado de
 *  vacaciones sobre los meses ya guardados; sólo admin) */
export async function getYearSchedulesOnce(year) {
  const snap = await getDocs(
    query(
      collection(db, "schedules"),
      where("month", ">=", `${year}-01`),
      where("month", "<=", `${year}-12`)
    )
  );
  return snap.docs.map((d) => d.data());
}

/** Todos los horarios de un año (para el contador de horas anuales) */
export function subscribeYearSchedules(year, onData, onError) {
  const q = query(
    collection(db, "schedules"),
    where("month", ">=", `${year}-01`),
    where("month", "<=", `${year}-12`)
  );
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => d.data())),
    onError
  );
}
